import { NgForOf } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnInit,
    Output,
    ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ProfileApiI } from "../../pojo/ProfileI";
import { TimeModelI } from "../../pojo/TimeModelI";
import { S25IconButtonComponent } from "../../standalone/s25.icon.button.component";
import { S25Util } from "../../util/s25-util";
import { Bind } from "../../decorators/bind.decorator";
import { Event } from "../../pojo/Event";
import { S25DatepickerModule } from "../s25-datepicker/s25.datepicker.module";
import { S25OccurrenceDatepickerComponent } from "../s25-datepicker/s25.occurrence.datepicker.component";
import { S25EditableModule } from "../s25-editable/s25.editable.module";
import { S25DatePatternComponent } from "./s25.datepattern.component";
import Occurrence = Event.Occurrence;

@Component({
    selector: "s25-ng-date-pattern-picker",
    template: `
        @if (this.init) {
            @if (changeType) {
                <label class="repeat-type-choice">
                    <span class="chooseText">Choose how you would like this to repeat:</span>
                    <select
                        class="cn-form__control"
                        [(ngModel)]="this.repeatType"
                        (change)="this.onPatternTypeChange($event)"
                    >
                        <option *ngFor="let type of RepeatTypes" [ngValue]="type">{{ type.itemName }}</option>
                    </select>
                </label>
            }
            @switch (this.repeatType?.itemTypeId) {
                @case (RepeatTypeId.Daily) {
                    <s25-ng-date-pattern
                        [type]="'daily'"
                        [(apiBean)]="this.dailyApiBean"
                        [timeModel]="this.timeModel"
                        [profileCode]="profileCode"
                        (apiBeanChange)="this.onApiChange($event); refreshCalendar()"
                    ></s25-ng-date-pattern>
                }

                @case (RepeatTypeId.Weekly) {
                    <s25-ng-date-pattern
                        [type]="'weekly'"
                        [(apiBean)]="this.weeklyApiBean"
                        [timeModel]="this.timeModel"
                        [profileCode]="profileCode"
                        (apiBeanChange)="this.onApiChange($event); refreshCalendar()"
                    ></s25-ng-date-pattern>
                }

                @case (RepeatTypeId.Monthly) {
                    <s25-ng-date-pattern
                        [type]="'monthly'"
                        [(apiBean)]="this.monthlyApiBean"
                        [timeModel]="this.timeModel"
                        [profileCode]="profileCode"
                        (apiBeanChange)="this.onApiChange($event); refreshCalendar()"
                    ></s25-ng-date-pattern>
                }
            }
            @if (
                (this.repeatType?.itemTypeId === RepeatTypeId.AdHoc && editAdHoc) ||
                (this.repeatType?.itemTypeId !== RepeatTypeId.AdHoc && editNonAdHoc)
            ) {
                <div
                    [class.s25-datepattern-picker--flex]="
                        this.repeatType?.itemTypeId === RepeatTypeId.AdHoc && showAdHocList
                    "
                    [class.non-adhoc-occurrences]="this.repeatType?.itemTypeId !== RepeatTypeId.AdHoc"
                >
                    <s25-ng-occurrence-datepicker
                        [firstDate]="$safeNavigationMigration(timeModel?.evStartDt)"
                        [(occurrences)]="adHocOccs"
                        [(apiBean)]="adHocApiBean"
                        [includeFirstDate]="
                            this.repeatType?.itemTypeId === RepeatTypeId.AdHoc ? includeFirstDate : true
                        "
                        (occurrencesChange)="onOccurrenceDatepickerChange($event)"
                        (apiBeanChange)="onApiChange($event)"
                        [validateDate]="validateAdHocDate"
                        [minDate]="minDate"
                        [maxDate]="maxDate"
                        [timeModel]="timeModel"
                    ></s25-ng-occurrence-datepicker>
                    @if (this.repeatType?.itemTypeId === RepeatTypeId.AdHoc && showAdHocList) {
                        <div class="c-floatRight c-margin-left--half c-margin-left--quarter ">
                            <label class="ngBold content-header">Ad Hoc Dates</label>
                            <div *ngFor="let occ of adHocOccs; index as i" class="s25-datepattern-picker--flex">
                                <s25-ng-editable-date [readOnly]="true" [val]="occ.evStartDt"></s25-ng-editable-date>
                                @if (adHocOccs.length > 1 && i > 0) {
                                    <s25-ng-icon-button
                                        class="c-svgIcon"
                                        [type]="'close'"
                                        [ariaLabel]="'Remove ' + occ"
                                        (clicked)="removeOcc(occ.evStartDt)"
                                    />
                                }
                            </div>
                        </div>
                    }
                </div>
            }
        }
    `,
    styles: `
        ::ng-deep .s25-datepattern-picker--flex {
            display: flex;
        }
        s25-ng-date-pattern {
            display: block;
            margin: 0 -0.75em;
        }
        .repeat-type-choice {
            display: flex;
            align-items: center;
            gap: 0.25em 0.5em;
            flex-wrap: wrap;
            margin: 0.25em 0 1em;
        }
        .non-adhoc-occurrences {
            display: block;
            margin-top: 1em;
        }
    `,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        NgForOf,
        S25DatepickerModule,
        S25EditableModule,
        S25IconButtonComponent,
        S25DatePatternComponent,
    ],
})
export class S25DatePatternPickerComponent implements OnInit {
    @Input() timeModel: TimeModelI;
    @Input() profileCode: string;
    @Input() adHocOccs: Occurrence[] = []; //Only used for ad hoc "patterns"
    @Input() editAdHoc = true; //We may not want to actually edit adHoc in this view
    @Input() showAdHocList: boolean = true; // Whether the date list should be shown next to the calendar
    @Input() editNonAdHoc: boolean = false; // Whether to show the calendar for non-ad-hoc patterns
    @Input() changeType = true;
    @Input() apiBean: any = {}; //ProfileApiI;
    @Input() validatePattern: Function;
    @Input() minDate?: Date;
    @Input() maxDate?: Date;
    @Input() includeFirstDate: boolean = false;
    @Output() apiBeanChange = new EventEmitter<ProfileApiI>();
    @Output() adHocOccsChange = new EventEmitter<Occurrence[]>();

    @ViewChild(S25OccurrenceDatepickerComponent) multiDatePicker: S25OccurrenceDatepickerComponent;

    origTimeModel: TimeModelI;
    init = false;
    repeatType?: RepeatType;

    dailyApiBean: ProfileApiI = {};
    weeklyApiBean: ProfileApiI = {};
    monthlyApiBean: ProfileApiI = {};
    @Input() adHocApiBean: ProfileApiI = {};

    protected readonly RepeatTypes = RepeatTypes;
    protected readonly RepeatTypeId = RepeatTypeId;

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
        private zone: NgZone,
    ) {}

    @Bind
    setPatternType(name: RepeatType["itemName"]) {
        this.repeatType = RepeatTypes.find((type) => type.itemName === name);
        this.sortAdHocOccs();
        this.cd.detectChanges();
    }

    onPatternTypeChange(obj?: any) {
        this.adHocOccs = [{ evStartDt: this.timeModel.evStartDt, evEndDt: this.timeModel.evEndDt }];
        this.refreshCalendar();
        this.onApiChange();
    }

    onApiChange(obj?: any) {
        this.sortAdHocOccs();
        this.cd.detectChanges();
        this.apiBeanChange.emit(this.apiBean);
    }

    onOccurrenceDatepickerChange(occs: Occurrence[]) {
        if (this.repeatType?.itemTypeId !== RepeatTypeId.AdHoc) {
            this.setPatternType("Ad hoc");
        }
        this.adHocOccsChange.emit(occs);
    }

    refreshCalendar() {
        const occs = this.getOccurrences();
        if (occs) this.adHocOccs = occs;
    }

    @Bind
    changeFirstDt(dt: Date) {
        if (this.profileCode === "adhoc") {
            // If ad-hoc, discard all dates before current date
            const occs = this.adHocOccs.filter((occ) => occ.evStartDt < dt);
            for (const occ of occs) this.removeOcc(occ.evStartDt);
        } else if (this.profileCode && this.profileCode !== "dnr") this.refreshCalendar();

        this.removeOcc(this.origTimeModel.evStartDt);
        this.multiDatePicker?.addOccurrence(dt);
        this.origTimeModel = S25Util.deepCopy(this.timeModel);
    }

    removeOcc(dt: any) {
        this.multiDatePicker?.removeOccurrence(dt);
    }

    validateAdHocDate = (dt: Date) => {
        return this.validatePattern ? this.validatePattern(dt) : true;
    };

    @Bind
    getProfileModel() {
        switch (this.repeatType?.itemTypeId) {
            case RepeatTypeId.AdHoc:
                return this.adHocApiBean.getProfileModel!();
            case RepeatTypeId.Daily:
                return this.dailyApiBean.getProfileModel!();
            case RepeatTypeId.Weekly:
                return this.weeklyApiBean.getProfileModel!();
            case RepeatTypeId.Monthly:
                return this.monthlyApiBean.getProfileModel!();
            case RepeatTypeId.DoesNotRepeat:
            default:
                return { type: "dnr" };
        }
    }

    @Bind
    getOccurrences() {
        switch (this.repeatType?.itemTypeId) {
            case RepeatTypeId.DoesNotRepeat:
                return [this.timeModel as Event.Occurrence];
            case RepeatTypeId.AdHoc:
                return this.adHocApiBean.getOccurrences?.();
            case RepeatTypeId.Daily:
                return this.dailyApiBean.getOccurrences?.();
            case RepeatTypeId.Weekly:
                return this.weeklyApiBean.getOccurrences?.();
            case RepeatTypeId.Monthly:
                return this.monthlyApiBean.getOccurrences?.();
        }
    }

    @Bind
    getProfileCode() {
        switch (this.repeatType?.itemTypeId) {
            case RepeatTypeId.AdHoc:
                return "adhoc";
            case RepeatTypeId.Daily:
                return this.dailyApiBean.getProfileCode!();
            case RepeatTypeId.Weekly:
                return this.weeklyApiBean.getProfileCode!();
            case RepeatTypeId.Monthly:
                return this.monthlyApiBean.getProfileCode!();
            case RepeatTypeId.DoesNotRepeat:
            default:
                return "dnr";
        }
    }

    sortAdHocOccs() {
        this.adHocOccs = this.adHocOccs.sort((a: any, b: any) => Date.parse(a.evStartDt) - Date.parse(b.evStartDt));
    }

    ngOnInit(): void {
        this.origTimeModel = S25Util.deepCopy(this.timeModel);
        this.sortAdHocOccs();

        if (!this.profileCode) {
            this.repeatType = RepeatTypes[0];
        } else if (this.profileCode === "dnr") {
            this.repeatType = RepeatTypes[0];
        } else if (this.profileCode === "adhoc") {
            this.repeatType = RepeatTypes[1];
        } else if (this.profileCode.startsWith("D")) {
            this.repeatType = RepeatTypes[2];
        } else if (this.profileCode.startsWith("W")) {
            this.repeatType = RepeatTypes[3];
        } else if (this.profileCode.startsWith("MD") || this.profileCode.startsWith("MP")) {
            this.repeatType = RepeatTypes[4];
        } else {
            this.repeatType = RepeatTypes[0];
        }

        this.apiBean.getProfileCode = this.getProfileCode;
        this.apiBean.getProfileModel = this.getProfileModel;
        this.apiBean.getOccurrences = this.getOccurrences;

        this.init = true;
        this.cd.detectChanges();
    }
}

export enum RepeatTypeId {
    DoesNotRepeat = -2,
    AdHoc = -1,
    Daily = 1,
    Weekly = 2,
    Monthly = 3,
}

export const RepeatTypes = [
    { itemTypeId: RepeatTypeId.DoesNotRepeat, itemName: "Does Not Repeat" },
    { itemTypeId: RepeatTypeId.AdHoc, itemName: "Ad hoc" },
    { itemTypeId: RepeatTypeId.Daily, itemName: "Daily" },
    { itemTypeId: RepeatTypeId.Weekly, itemName: "Weekly" },
    { itemTypeId: RepeatTypeId.Monthly, itemName: "Monthly" },
] as const;

export type RepeatType = (typeof RepeatTypes)[number];
